import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import { AccessDenied } from '@/components/AccessDenied'
import { useAuth } from '@/lib/auth/auth-context'
import { canAddCompanies, canOpenPage } from '@/lib/auth/roles'
import { cx } from '@/lib/browser/class-names'
import type { PortfolioRow } from '@/lib/api/endpoints/portfolio'
import { isForbiddenError } from '@/lib/api/errors'
import {
  dotSize,
  QUADRANT_BACKGROUND,
  QUADRANT_COLOR,
  QUADRANT_DESCRIPTION,
  QUADRANT_LABEL,
  QUADRANT_ORDER,
  UNSCORED_BACKGROUND,
  UNSCORED_COLOR,
  urgencyColor,
} from '@/lib/scoring/presentation'

import { DEFAULT_SORT, nextSort, sortRows, type SortField, type SortState } from './sort-rows'
import { pointKey, tieFans } from './tie-fan'
import { usePortfolioOverview } from './use-portfolio'

const FAN_LINGER = 140

const SORT_COLUMNS: readonly { field: SortField; label: string }[] = [
  { field: 'name', label: 'Company' },
  { field: 'sector', label: 'Vertical' },
  { field: 'quadrant', label: 'Quadrant' },
  { field: 'urgency', label: 'Urgency' },
  { field: 'def', label: 'Defensibility' },
  { field: 'rdy', label: 'Readiness' },
]

export function Portfolio() {
  const navigate = useNavigate()
  const { session } = useAuth()
  const [quarter, setQuarter] = useState<string | undefined>(undefined)
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)

  const role = session?.role ?? null
  const overview = usePortfolioOverview(quarter, canOpenPage(role, 'portfolio'))

  function toggleSort(field: SortField) {
    setSort((current) => nextSort(current, field))
  }

  function openCompany(id: string) {
    navigate(`/company/${encodeURIComponent(id)}`)
  }

  if (!canOpenPage(role, 'portfolio')) {
    return <Navigate to="/company" replace />
  }

  if (isForbiddenError(overview.error)) {
    return (
      <AccessDenied
        title="Portfolio AI Intelligence"
        detail="This screen aggregates the whole portfolio, so it is available to the growth team and to partners. Your role sees individual companies instead — open one from the Company tab."
      />
    )
  }

  if (overview.isPending) {
    return (
      <div className="canopy-page">
        <div className="canopy-page-header">
          <div>
            <h1 className="canopy-page-title">Portfolio AI Intelligence</h1>
            <div className="canopy-page-sub">Loading…</div>
          </div>
        </div>
      </div>
    )
  }

  if (overview.isError) {
    return (
      <div className="canopy-page">
        <div className="canopy-page-header">
          <div>
            <h1 className="canopy-page-title">Portfolio AI Intelligence</h1>
            <div className="canopy-page-sub" style={{ color: 'var(--red-600)' }} role="alert">
              Could not load the portfolio. Try again in a moment.
            </div>
          </div>
        </div>
      </div>
    )
  }

  const { metrics, companies, quarters } = overview.data
  const scored = companies.filter((c) => c.scores !== null)

  const rows = sortRows(companies, sort)

  if (companies.length === 0) {
    const mayAdd = canAddCompanies(role)

    return (
      <div className="canopy-page">
        <div className="canopy-page-header">
          <div>
            <h1 className="canopy-page-title">Portfolio AI Intelligence</h1>
            <div className="canopy-page-sub">
              {mayAdd ? 'No companies yet — get started by importing them.' : 'No companies yet.'}
            </div>
          </div>
        </div>
        <div className="canopy-card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 12 }}>
            The portfolio is empty.
          </div>
          {mayAdd ? (
            <Link to="/admin/companies" className="canopy-btn canopy-btn-primary">
              Add companies →
            </Link>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.5 }}>
              Adding companies is the growth team&rsquo;s to do. Ask them to import the portfolio,
              and it will show up here.
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="canopy-page">
      <div className="canopy-page-header">
        <div>
          <h1 className="canopy-page-title">Portfolio AI Intelligence</h1>
          <div className="canopy-page-sub">
            {metrics.scoredCompanies} of {metrics.totalCompanies} companies scored · Scores follow
            AI Canopy Scoring Matrix (May 2026)
          </div>
        </div>

        {quarters.length > 0 && (
          <label className="canopy-ctrl-row" style={{ fontSize: 11, color: 'var(--text-3)' }}>
            <select
              className="canopy-select-ctrl"
              value={quarter ?? overview.data.quarter ?? ''}
              onChange={(event) => {
                setQuarter(event.target.value)
              }}
              aria-label="Quarter filter"
            >
              {quarters.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="canopy-metric-row">
        <div className="canopy-metric-card">
          <div className="canopy-metric-label">Companies scored</div>
          <div className="canopy-metric-value">{metrics.scoredCompanies}</div>
          {metrics.pendingCompanies > 0 && (
            <div className="canopy-metric-delta">{metrics.pendingCompanies} pending</div>
          )}
        </div>
        <div className="canopy-metric-card">
          <div className="canopy-metric-label">Avg defensibility</div>
          <div className="canopy-metric-value">{metrics.avgDefensibility}</div>
          <div className="canopy-metric-delta">/ 100</div>
        </div>
        <div className="canopy-metric-card">
          <div className="canopy-metric-label">Avg readiness</div>
          <div className="canopy-metric-value">{metrics.avgReadiness}</div>
          <div className="canopy-metric-delta">/ 100</div>
        </div>
        <div className="canopy-metric-card">
          <div className="canopy-metric-label">Operational edge</div>
          <div className="canopy-metric-value canopy-up">{metrics.operationalEdgeCount}</div>
          <div className="canopy-metric-delta">protected + executing</div>
        </div>
        <div className="canopy-metric-card">
          <div className="canopy-metric-label">Emergency upgrade</div>
          <div className="canopy-metric-value canopy-down">{metrics.emergencyUpgradeCount}</div>
          <div className="canopy-metric-delta">immediate action needed</div>
        </div>
      </div>

      <div className="canopy-quadrant-wrap">
        <div className="canopy-section-title">
          <span>Urgency quadrant — defensibility (Y) vs readiness (X)</span>
          {scored.length > 0 && (
            <span className="canopy-section-hint">click a company to open detail</span>
          )}
        </div>
        <QuadrantChart companies={scored} onOpen={openCompany} />

        <div className="canopy-q-legend">
          {QUADRANT_ORDER.map((q) => (
            <div key={q} className="canopy-q-leg-item">
              <div className="canopy-q-leg-dot" style={{ background: QUADRANT_COLOR[q] }} />
              {QUADRANT_LABEL[q]}
              <span style={{ color: 'var(--text-3)', fontSize: 10 }}>
                {' '}
                — {QUADRANT_DESCRIPTION[q]}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="canopy-table-wrap">
        <div className="canopy-table-header">
          <div className="canopy-section-title" style={{ marginBottom: 0 }}>
            All companies
          </div>
          <div className="canopy-ctrl-row">
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
              Click a row to open company view
            </span>
          </div>
        </div>
        <table className="canopy-table">
          <thead>
            <tr>
              {SORT_COLUMNS.map((column) => {
                const isSorted = sort.field === column.field
                const ascending = isSorted && sort.direction === 'asc'

                const className = cx(isSorted && 'sorted', ascending && 'asc')

                return (
                  <th
                    key={column.field}
                    className={className}
                    aria-sort={isSorted ? (ascending ? 'ascending' : 'descending') : 'none'}
                    onClick={() => {
                      toggleSort(column.field)
                    }}
                  >
                    {column.label}
                  </th>
                )
              })}
              <th>Top action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((company) => (
              <CompanyRow
                key={company.id}
                company={company}
                onOpen={() => {
                  openCompany(company.id)
                }}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function QuadrantChart({
  companies,
  onOpen,
}: {
  companies: readonly PortfolioRow[]
  onOpen: (id: string) => void
}) {
  const [openFan, setOpenFan] = useState<string | null>(null)
  const closing = useRef<number | null>(null)

  const fans = useMemo(
    () =>
      tieFans(
        companies.flatMap((company) =>
          company.scores
            ? [
                {
                  id: company.id,
                  x: company.scores.dot.x,
                  y: company.scores.dot.y,
                  radius: dotSize(company.scores.urgency) / 2,
                },
              ]
            : [],
        ),
      ),
    [companies],
  )

  function cancelClose() {
    if (closing.current === null) return

    clearTimeout(closing.current)
    closing.current = null
  }

  function openStack(key: string | null) {
    cancelClose()
    setOpenFan(key)
  }

  function closeStack() {
    cancelClose()
    closing.current = window.setTimeout(() => {
      closing.current = null
      setOpenFan(null)
    }, FAN_LINGER)
  }

  useEffect(() => cancelClose, [])

  const opened = openFan === null ? undefined : fans.get(openFan)

  return (
    <div className="canopy-quadrant-chart">
      <div className="canopy-q-zone" style={{ top: 0, left: 0, width: '50%', height: '50%' }}>
        <div className="canopy-q-zone-name" style={{ color: 'var(--blue-800)' }}>
          Monitor
        </div>
        <div className="canopy-q-zone-desc" style={{ color: 'var(--blue-600)' }}>
          High def · low readiness
        </div>
      </div>
      <div className="canopy-q-zone" style={{ top: 0, right: 0, width: '50%', height: '50%' }}>
        <div className="canopy-q-zone-name" style={{ color: 'var(--green-800)' }}>
          Operational edge
        </div>
        <div className="canopy-q-zone-desc" style={{ color: 'var(--green-600)' }}>
          High def · high readiness
        </div>
      </div>
      <div className="canopy-q-zone" style={{ bottom: 0, left: 0, width: '50%', height: '50%' }}>
        <div className="canopy-q-zone-name" style={{ color: 'var(--red-800)' }}>
          Emergency upgrade
        </div>
        <div className="canopy-q-zone-desc" style={{ color: 'var(--red-600)' }}>
          Low def · low readiness
        </div>
      </div>
      <div className="canopy-q-zone" style={{ bottom: 0, right: 0, width: '50%', height: '50%' }}>
        <div className="canopy-q-zone-name" style={{ color: 'var(--amber-800)' }}>
          Accelerate
        </div>
        <div className="canopy-q-zone-desc" style={{ color: 'var(--amber-600)' }}>
          Low def · high readiness
        </div>
      </div>

      <div className="canopy-q-axis-h" />
      <div className="canopy-q-axis-v" />
      <div className="canopy-q-axis-label" style={{ bottom: -20, left: 0 }}>
        ← Low readiness
      </div>
      <div className="canopy-q-axis-label" style={{ bottom: -20, right: 0 }}>
        High readiness →
      </div>

      <div
        className="canopy-q-axis-label"
        style={{
          left: -14,
          top: '50%',
          writingMode: 'vertical-rl',
          transform: 'translateY(-50%) rotate(180deg)',
          fontSize: 9,
        }}
      >
        High def →
      </div>

      {companies.length === 0 && (
        <div className="canopy-q-empty" role="status">
          <div className="canopy-q-empty-title">Nothing to plot yet</div>
          <div>
            No company has a scored snapshot for this quarter. Every one of them is in the table
            below, marked “No snapshot yet”.
          </div>
        </div>
      )}

      {opened && (
        <div
          className="canopy-q-fan-tint"
          style={{
            left: `${String(opened.point.x)}%`,
            top: `${String(opened.point.y)}%`,
            width: opened.bounds.left + opened.bounds.right,
            height: opened.bounds.height,
            transform: `translate(calc(-50% + ${String((opened.bounds.right - opened.bounds.left) / 2)}px), -50%)`,
          }}
        />
      )}

      {companies.map((company) => {
        const scores = company.scores
        if (!scores) return null

        const quadrantColor = QUADRANT_COLOR[scores.quadrant]
        const key = pointKey(scores.dot.x, scores.dot.y)
        const fan = fans.get(key)
        const fanned = fan && openFan === key
        const offset = fanned ? fan.offsets.get(company.id) : undefined

        return (
          <button
            key={company.id}
            type="button"
            className={cx(
              'canopy-q-dot',
              scores.quadrant === 'emergency_upgrade' && 'canopy-q-dot-critical',
              fan?.ids[0] === company.id && 'canopy-q-dot-front',
              fanned && 'canopy-q-dot-tied',
            )}
            onMouseEnter={() => {
              openStack(fan ? key : null)
            }}
            onMouseLeave={closeStack}
            onFocus={() => {
              openStack(fan ? key : null)
            }}
            onBlur={closeStack}
            style={
              {
                left: `${String(scores.dot.x)}%`,
                top: `${String(scores.dot.y)}%`,
                background: quadrantColor,
                '--dot-size': `${String(dotSize(scores.urgency))}px`,
                '--dot-halo': quadrantColor,
                '--dx': `${String(offset?.dx ?? 0)}px`,
                '--dy': `${String(offset?.dy ?? 0)}px`,
              } as CSSProperties
            }
            onClick={() => {
              onOpen(company.id)
            }}
            aria-label={`${company.name} — defensibility ${String(scores.def)}, readiness ${String(scores.rdy)}, urgency ${String(scores.urgency)}`}
          >
            {company.initials}
            <span className="canopy-q-tooltip">
              {company.name} · Def {scores.def} · Rdy {scores.rdy} · Urgency {scores.urgency}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function CompanyRow({ company, onOpen }: { company: PortfolioRow; onOpen: () => void }) {
  const scores = company.scores

  return (
    <tr
      onClick={onOpen}

      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen()
        }
      }}
    >
      <td>
        <div className="canopy-company-cell">
          <div
            className="canopy-company-init"
            style={{
              background: scores ? QUADRANT_BACKGROUND[scores.quadrant] : UNSCORED_BACKGROUND,
              color: scores ? QUADRANT_COLOR[scores.quadrant] : UNSCORED_COLOR,
            }}
          >
            {company.initials}
          </div>
          <div className="canopy-company-name">{company.name}</div>
        </div>
      </td>
      <td>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{company.sector ?? '—'}</span>
      </td>
      {scores ? (
        <>
          <td>
            <span className={`canopy-tag canopy-tag-${scores.quadrant}`}>
              {QUADRANT_LABEL[scores.quadrant]}
            </span>
          </td>
          <td>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: urgencyColor(scores.urgency),
                  fontFamily: 'var(--mono)',
                }}
              >
                {scores.urgency}
              </span>
              <div className="canopy-score-bar-track" style={{ maxWidth: 50 }}>
                <div
                  className="canopy-score-bar-fill"
                  style={{
                    width: `${String(scores.urgency)}%`,
                    background: urgencyColor(scores.urgency),
                  }}
                />
              </div>
            </div>
          </td>
          <td>
            <div className="canopy-score-bar-cell">
              <div className="canopy-score-bar-track">
                <div
                  className="canopy-score-bar-fill"
                  style={{ width: `${String(scores.def)}%`, background: 'var(--green-400)' }}
                />
              </div>
              <div className="canopy-score-num">{scores.def}</div>
            </div>
          </td>
          <td>
            <div className="canopy-score-bar-cell">
              <div className="canopy-score-bar-track">
                <div
                  className="canopy-score-bar-fill"
                  style={{ width: `${String(scores.rdy)}%`, background: 'var(--blue-400)' }}
                />
              </div>
              <div className="canopy-score-num">{scores.rdy}</div>
            </div>
          </td>
          <td>
            <span style={{ fontSize: 11, color: 'var(--text-2)' }}>{company.topAction ?? '—'}</span>
          </td>
        </>
      ) : (
        <td colSpan={5} style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>
          No snapshot yet
        </td>
      )}
    </tr>
  )
}
